import * as signalR from "@microsoft/signalr";
import { useOrderStore } from "@/stores/OrderStore";
import { useProductStore } from "@/stores/productsStore";

let connection = null;
let messageHandlers = [];

// branch registration state
let registeredBranchId = null;
let isBranchRegistered = false;

// Cross-tab coordination
let broadcastChannel = null;
const processedMessages = new Set();
const MAX_PROCESSED_MESSAGES = 100;

/**
 * Initialize BroadcastChannel for cross-tab sync
 */
function initBroadcastChannel() {
  if (typeof BroadcastChannel === 'undefined') {
    console.log("SignalR: BroadcastChannel not supported");
    return;
  }

  try {
    broadcastChannel = new BroadcastChannel('pos-realtime-sync');
    
    broadcastChannel.onmessage = (event) => {
      const { type, messageId, payload } = event.data || {};

      if (!payload) return;

      console.log("SignalR: Received cross-tab message", type, messageId);

      // Skip if already processed
      if (messageId && processedMessages.has(messageId)) {
        console.log("SignalR: Skipping duplicate cross-tab message", messageId);
        return;
      }

      if (type === 'REALTIME_DELETE') {
        const productStore = useProductStore();
        productStore.applyRealtimeProductDelete(payload);

        const orderStore = useOrderStore();
        orderStore.applyRealtimeItemDelete(payload);
      } else if (type === 'REALTIME_UPDATE') {
        // Apply update from other tab
        const productStore = useProductStore();
        productStore.applyRealtimeProductUpdate(payload);

        const orderStore = useOrderStore();
        const didUpdate = orderStore.applyRealtimeItemUpdate(payload);
        if (didUpdate) {
          orderStore.saveOrderItemsToLocalStorage();
        }
      } else if (type === 'REALTIME_RULES') {
        const productStore = useProductStore();
        productStore.applyRealtimeRuleUpdate(payload);
      }
        
        if (messageId) {
          markMessageProcessed(messageId);
        }
    };
    
    console.log("SignalR: BroadcastChannel initialized");
  } catch (e) {
    console.warn("SignalR: BroadcastChannel failed to initialize", e);
  }
}

/**
 * Broadcast message to other tabs
 */
function broadcastToOtherTabs(messageId, payload, type = 'REALTIME_UPDATE') {
  if (!broadcastChannel) return;

  try {
    broadcastChannel.postMessage({
      type,
      messageId,
      payload
    });
  } catch (e) {
    console.warn("SignalR: Failed to broadcast to other tabs", e);
  }
}

/**
 * Check if message was already processed
 */
function hasProcessedMessage(id) {
  return processedMessages.has(id);
}

/**
 * Mark message as processed (with size limit)
 */
function markMessageProcessed(id) {
  processedMessages.add(id);
  if (processedMessages.size > MAX_PROCESSED_MESSAGES) {
    const first = processedMessages.values().next().value;
    processedMessages.delete(first);
  }
}

/**
 * Starts the SignalR realtime connection.
 * Must be called AFTER login (JWT available) AND branchId is known.
 *
 * @param {Function} getToken - function that returns current JWT
 * @param {Function} getBranchContext - function that returns { branchId, lastUpdatedOn? }
 */
export function startRealtime(getToken, getBranchContext) {
  if (!getBranchContext) {
    throw new Error("SignalR: getBranchContext is required");
  }

  if (connection) {
    console.log("SignalR: Connection already exists, skipping start");
    return connection;
  }

  // Initialize cross-tab communication
  initBroadcastChannel();

  console.log("SignalR: Starting new connection...");
  const token = getToken();
  console.log("SignalR: Token available:", !!token);

  connection = new signalR.HubConnectionBuilder()
    .withUrl("/hubs/pos", {
      accessTokenFactory: () => {
        const t = getToken();
        console.log("SignalR: Providing token to handshake:", !!t);
        return t;
      }
    })
    .withAutomaticReconnect()
    .build();

  connection.on("message", msg => {
    const text = typeof msg === "string" ? msg : JSON.stringify(msg, null, 2);
    console.log("SignalR: Received message:", text);

    if (msg?.type === 'DataProductDeleted') {
      deleteItem(msg);
    } else if (msg?.type === 'DataPricingRulesUpdated') {
      updateRules(msg);
    } else {
      updateItem(msg);
    }

    messageHandlers.forEach(h => h(msg));
  });

  connection.onreconnecting(err => {
    console.warn("SignalR: Reconnecting...", err);
  });

  connection.onreconnected(() => {
    console.log("SignalR: Reconnected");
    // SignalR groups are not guaranteed after reconnect -> re-register
    isBranchRegistered = false;
    registerBranch(getBranchContext);
  });

  connection.onclose(err => {
    console.warn("SignalR: Connection closed", err);
    isBranchRegistered = false;
  });

  connection
    .start()
    .then(() => {
      console.log("SignalR: ✅ Connected successfully");
      registerBranch(getBranchContext);
    })
    .catch(err => console.error("SignalR: ❌ Failed to connect", err));

  return connection;
}

/**
 * Registers this connection to a branch group.
 * Requires server hub method: RegisterBranches(IEnumerable<Guid> branchIds, string lastUpdatedOn)
 */
function registerBranch(getBranchContext) {
  console.log("SignalR: registerBranch() called");

  if (!connection) {
    console.warn("SignalR: ⚠️ Cannot register - no connection");
    return;
  }

  const ctx = getBranchContext?.();
  console.log("SignalR: branch context =", ctx);

  const branchId = ctx?.branchId;
  const lastUpdatedOn = ctx?.lastUpdatedOn;

  if (!branchId) {
    console.warn("SignalR: ⚠️ Cannot register - missing branchId in context");
    return;
  }

  // Avoid re-register spam if already registered with same branch
  if (isBranchRegistered && registeredBranchId === branchId) {
    console.log("SignalR: Branch already registered, skipping");
    return;
  }

  // Convert lastUpdatedOn to ISO string if it exists
  const lastUpdatedOnISO = lastUpdatedOn 
    ? (lastUpdatedOn instanceof Date ? lastUpdatedOn.toISOString() : lastUpdatedOn)
    : null;

  console.log("SignalR: invoking RegisterBranches with", {
    branchIds: branchId,
    lastUpdatedOn: lastUpdatedOnISO
  });

  connection.invoke("RegisterBranches", branchId, lastUpdatedOnISO)
    .then(() => {
      registeredBranchId = branchId;
      isBranchRegistered = true;
      console.log("SignalR: ✅ branch registered");
    })
    .catch(err => {
      console.error("SignalR: ❌ RegisterBranches failed", err);
    });
}

/**
 * Stops the SignalR connection and cleans up state.
 */
export function stopRealtime() {
  if (!connection) {
    console.log("SignalR: No connection to stop");
    return;
  }

  console.log("SignalR: Stopping connection...");
  connection.stop();
  connection = null;
  messageHandlers = [];

  registeredBranchId = null;
  isBranchRegistered = false;

  // Clean up BroadcastChannel
  if (broadcastChannel) {
    broadcastChannel.close();
    broadcastChannel = null;
  }
  processedMessages.clear();

  console.log("SignalR: Connection stopped");
}

/**
 * Optional helper: call this if branch changes while app is running.
 * Example: user switches branch without full logout.
 */
export async function updateBranchRegistration(getBranchContext) {
  if (!connection) {
    console.warn("SignalR: ⚠️ Cannot update registration - no connection");
    return;
  }

  isBranchRegistered = false;
  registerBranch(getBranchContext);
}

/**
 * Registers a handler for realtime messages.
 *
 * @param {Function} handler
 */
export function onRealtimeMessage(handler) {
  messageHandlers.push(handler);

  return () => {
    messageHandlers = messageHandlers.filter(h => h !== handler);
  };
}

/**
 * Checks if SignalR is currently connected.
 */
export function isRealtimeConnected() {
  return connection !== null;
}

/**
 * Remove a deleted product from the catalog and barcode cache.
 * Handles deduplication and cross-tab broadcasting.
 */
function deleteItem(msg) {
  const payload = msg?.payload;
  if (!payload) return;

  const messageId = msg?.messageId || `delete-${payload.Sku}-${Date.now()}`;

  if (hasProcessedMessage(messageId)) {
    console.log("SignalR: Skipping duplicate delete message", messageId);
    return;
  }

  const productStore = useProductStore();
  productStore.applyRealtimeProductDelete(payload);

  const orderStore = useOrderStore();
  orderStore.applyRealtimeItemDelete(payload);

  broadcastToOtherTabs(messageId, payload, 'REALTIME_DELETE');

  markMessageProcessed(messageId);
}

/**
 * Replace one pricing rule's rows in the catalogue store (DataPricingRulesUpdated).
 * Line prices are never changed by this — only the badge re-evaluates.
 */
function updateRules(msg) {
  const payload = msg?.payload;
  if (!payload || !payload.externalId) return;

  const messageId = msg?.messageId || msg?.correlationId || `rules-${payload.externalId}-${msg?.issuedAtUtc || Date.now()}`;
  if (hasProcessedMessage(messageId)) {
    console.log("SignalR: Skipping duplicate rules message", messageId);
    return;
  }

  const productStore = useProductStore();
  productStore.applyRealtimeRuleUpdate(payload);

  broadcastToOtherTabs(messageId, payload, 'REALTIME_RULES');
  markMessageProcessed(messageId);
}

/**
 * Update order item based on realtime message.
 * Handles deduplication and cross-tab broadcasting.
 */
function updateItem(msg) {
  const payload = msg?.payload;
  if (!payload) {
    return;
  }

  // Generate or use existing message ID for deduplication
  const messageId = msg?.messageId || `${payload.itemId}-${payload.updatedAt || Date.now()}`;

  // Skip if already processed (from SignalR or cross-tab)
  if (hasProcessedMessage(messageId)) {
    console.log("SignalR: Skipping duplicate message", messageId);
    return;
  }

  // Update the product catalog (all products list)
  const productStore = useProductStore();
  productStore.applyRealtimeProductUpdate(payload);

  // Update any matching items currently in the open order
  const orderStore = useOrderStore();
  const didUpdate = orderStore.applyRealtimeItemUpdate(payload);

  if (didUpdate) {
    orderStore.saveOrderItemsToLocalStorage();

    // Broadcast to other tabs on same computer
    broadcastToOtherTabs(messageId, payload);
  }

  markMessageProcessed(messageId);
}
